import { useEffect } from 'react';
import { useParams, useRouterState } from '@tanstack/react-router';
import { t } from '@lingui/core/macro';
import { useFolderByRouteId } from './useFolderByRouteId';
import { useTagBySlug } from './useTagBySlug';
import { getSection } from '../services/docs';
import {
  activeGroupFromPath,
  developerVersionFromPath,
  getGroupLabel,
  type DocGroupKey,
} from '../components/Docs/docGroupRoutes';

const BASE_TITLE = 'Instapaper';
const DOCS_BASE_TITLE = 'Instapaper Docs';

// Per-group document-title base. Documentation keeps the established
// "Instapaper Docs"; the other groups read "Instapaper <group label>"
// (e.g. "Instapaper Developers", "Instapaper About & Legal").
function getDocsBaseTitle(group: DocGroupKey): string {
  if (group === 'documentation') return DOCS_BASE_TITLE;
  return `${BASE_TITLE} ${getGroupLabel(group)}`;
}

function extractSlug(pathname: string): string {
  return pathname.split('/')[2] ?? '';
}

/** Legacy landing page titles (no " - Instapaper" suffix). */
function getStaticPageTitle(pathname: string): string | null {
  // The marketing landing page (guests at `/`) is titled just "Instapaper".
  if (pathname === '/') {
    return BASE_TITLE;
  }
  if (pathname === '/iphone') {
    return 'Award-winning iPhone/iPad app for offline reading' + ' - ' + BASE_TITLE;
  }
  if (pathname === '/android') {
    return BASE_TITLE;
  }
  // /notion, /pocket, /kobo are intentionally untranslated (plain strings) and
  // mirror the prerendered <title> in vite-plugins/static-prerender.ts.
  if (pathname === '/notion') {
    return 'Instapaper Notion Integration';
  }
  if (pathname === '/pocket') {
    return 'Welcome Pocket Fans';
  }
  if (pathname === '/kobo') {
    return 'Instapaper is now live on Rakuten Kobo eReaders!';
  }
  return null;
}

// `/developers/applications/*` are real React routes, not markdown sections, so
// the docs section lookup can't name them. Map each subroute to a title that
// matches its on-page heading. Returns null for any non-applications path.
function getApplicationsTitle(parts: string[]): string | null {
  const [, section, sub, action] = parts;
  if (section !== 'applications') return null;
  if (!sub) return 'Applications';
  if (sub === 'create') return 'Register an application';
  if (sub === 'claim') return 'Claim an application';
  if (action === 'edit') return 'Edit application';
  return 'Applications';
}

// Full document title for any docs-center route: "<Page or Section> - <group
// base>" (e.g. "... - Instapaper About & Legal"). A group root (and unknown
// sections) falls back to just the group base. Works across all groups
// (Documentation, Developers, About & Legal), whose paths are
// `/<group>/<section>?/<slug>?`.
export function getDocsTitle(pathname: string): string {
  const group = activeGroupFromPath(pathname);
  if (!group) return DOCS_BASE_TITLE;

  const baseTitle = getDocsBaseTitle(group);

  const parts = pathname.split('/').filter(Boolean); // [group, section?, slug?]

  const applicationsTitle = getApplicationsTitle(parts);
  if (applicationsTitle) return `${applicationsTitle} - ${baseTitle}`;

  // Versioned developer paths insert a version segment (/developers/v1/...), so
  // section/page shift one position right and the lookup is scoped to the version.
  const version = group === 'developers' ? developerVersionFromPath(pathname) : undefined;
  const sectionSlug = version ? parts[2] : parts[1];
  const pageSlug = version ? parts[3] : parts[2];
  if (!sectionSlug) return baseTitle;

  const section = getSection(sectionSlug, group, version);
  if (!section) return baseTitle;

  const name = pageSlug
    ? (section.pages.find((p) => p.slug === pageSlug)?.title ?? section.title)
    : section.title;
  return `${name} - ${baseTitle}`;
}

// Note, no check for '/read' beccause that will be handled by 'useArticleDocumentTitle'
function getSectionTitle(pathname: string): string {
  // Note: `/` is handled earlier by getStaticPageTitle (titled just "Instapaper").
  if (pathname.startsWith('/home')) return t`Home`;
  if (pathname.startsWith('/liked')) return t`Liked`;
  if (pathname.startsWith('/archive')) return t`Archive`;
  if (pathname.startsWith('/videos')) return t`Videos`;
  if (pathname.startsWith('/notes')) return t`Notes`;
  if (pathname.startsWith('/search')) return t`Search`;
  if (pathname.startsWith('/settings')) return t`Settings`;
  return '';
}

/**
 * Sets the document title based on the current route.
 * Called from the root component to provide section-level titles.
 */
export function useDocumentTitle() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const slug = extractSlug(pathname);
  const params = useParams({ strict: false });
  const folder = useFolderByRouteId(
    pathname.startsWith('/folder/') ? (params.folderId ?? '') : '',
    params.slug === undefined,
  );
  const tag = useTagBySlug(pathname.startsWith('/tags/') ? slug : '');

  useEffect(() => {
    const staticTitle = getStaticPageTitle(pathname);
    if (staticTitle !== null) {
      document.title = staticTitle;
      return;
    }

    if (activeGroupFromPath(pathname)) {
      document.title = getDocsTitle(pathname);
      return;
    }

    let section = getSectionTitle(pathname);
    if (!section && pathname.startsWith('/folder/') && folder) {
      section = folder.displayTitle;
    }
    if (!section && pathname.startsWith('/tags/') && tag) {
      section = tag.name;
    }
    document.title = section ? `${section} - ${BASE_TITLE}` : BASE_TITLE;
  }, [pathname, folder, tag]);
}

/**
 * Overrides the document title to include an article title.
 * Called from the reader component when an article is open.
 */
export function useArticleDocumentTitle(articleTitle: string | undefined) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isReadRoute = pathname.includes('/read/');

  useEffect(() => {
    if (!isReadRoute || !articleTitle) return;
    document.title = `${articleTitle} - ${BASE_TITLE}`;
  }, [articleTitle, pathname, isReadRoute]);
}
